import React, { useEffect, useState } from "react";
import { salesOrderRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { DataTable, PageHead, Select, StatusBadge, baht, formatDate, useAsync, useTableState } from "../../components/ui";

const SHIP_OPTIONS = ["pending", "packing", "shipped", "delivered", "cancelled"];
const CHANNEL_ICON = { line: "🟢", facebook: "🔵", instagram: "🟣", live: "🔴", walkin: "🏬" };

// เลขพัสดุ: พิมพ์แล้วกด Enter / คลิกออกจากช่องเพื่อบันทึก
function TrackingCell({ row, onSave }) {
  const [value, setValue] = useState(row.trackingNo || "");
  useEffect(() => setValue(row.trackingNo || ""), [row.trackingNo]);
  const commit = () => {
    const next = value.trim();
    if (next !== (row.trackingNo || "")) onSave(row, next);
    else setValue(next);
  };
  return (
    <input
      className="input-crm"
      style={{ width: 150, padding: "3px 8px" }}
      value={value}
      maxLength={40}
      placeholder="-"
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}

export default function Orders() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [shipStatus, setShipStatus] = useState("");

  const { data, loading, reload } = useAsync(
    () =>
      salesOrderRepo.list(orgId, {
        page: tableState.page,
        perPage: tableState.perPage,
        search: tableState.search,
        filter: (row) => !shipStatus || row.shippingStatus === shipStatus,
      }),
    [orgId, shipStatus, tableState.page, tableState.perPage, tableState.search]
  );

  const updateShipping = async (row, status) => {
    await salesOrderRepo.update(orgId, row.id, { shippingStatus: status });
    reload();
    toast(tx("อัปเดตสถานะจัดส่งแล้ว", "Shipping status updated"), "success");
  };

  const updateTracking = async (row, trackingNo) => {
    try {
      await salesOrderRepo.update(orgId, row.id, { trackingNo });
      reload();
      toast(tx("บันทึกเลขพัสดุแล้ว", "Tracking number saved"), "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <>
      <PageHead title={tx("จัดการออเดอร์", "Order management")} subtitle={tx("รวมออเดอร์จากแชท ไลฟ์สด และหน้าร้านออนไลน์", "Orders from chat, live and online storefront")} />

      <div className="filter-bar">
        <input className="input-crm" style={{ maxWidth: 260 }} placeholder={tx("ค้นหาเลขออเดอร์ / ลูกค้า", "Search order / customer")} value={tableState.search} onChange={(e) => tableState.setSearch(e.target.value)} />
        <Select options={SHIP_OPTIONS} value={shipStatus} onChange={(e) => setShipStatus(e.target.value)} placeholder={tx("ทุกสถานะจัดส่ง", "All shipping status")} style={{ width: "auto" }} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีออเดอร์", "No orders")}
        columns={[
          { key: "orderNo", label: tx("เลขออเดอร์", "Order no.") },
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "channel", label: tx("ช่องทาง", "Channel"), render: (row) => <span>{CHANNEL_ICON[row.channel]} {row.channel}</span> },
          { key: "total", label: tx("ยอดรวม", "Total"), align: "right", render: (row) => baht(row.total) },
          { key: "paymentStatus", label: tx("การชำระ", "Payment"), render: (row) => <StatusBadge status={row.paymentStatus} /> },
          {
            key: "shippingStatus",
            label: tx("การจัดส่ง", "Shipping"),
            render: (row) => (
              <select className="select-crm" style={{ width: "auto", padding: "3px 8px" }} value={row.shippingStatus} onChange={(e) => updateShipping(row, e.target.value)}>
                {SHIP_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ),
          },
          { key: "trackingNo", label: tx("เลขพัสดุ", "Tracking"), render: (row) => <TrackingCell row={row} onSave={updateTracking} /> },
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt) },
        ]}
      />
    </>
  );
}
